import React, { useCallback, useRef, useState } from "react";
import {
  makeStyles,
  Popover,
  ButtonBase,
  Typography,
  Button,
  Grid,
  OutlinedInput,
  List,
  ListItem,
  ListItemText,
  CircularProgress
} from "@material-ui/core";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import { ReactComponent as RoutetitanLogo } from "../../assets/images/icons/logo.svg";
import clsx from "clsx";
import { useHistory } from "react-router-dom";
import API from "../../utils/axios";
import DateFnsUtils from "@date-io/date-fns";
import { useSnackbar } from "notistack";
import useProductTour from "../../states/tour/tours/product";
import { FormattedText } from "../common/FormattedText";

const useStyles = makeStyles(theme => ({
  menuButton: {
    borderRadius: 4,
    "&:hover": {
      backgroundColor: "#F4F4F4"
    }
  },
  menuButtonOpen: {
    backgroundColor: "#F4F4F4"
  },
  routetitanLogo: {
    width: 24,
    height: 24,
    padding: 4
  },
  projectsMenuArrowIcon: {
    fontSize: theme.typography.pxToRem(16),
    marginLeft: -4,
    marginRight: -4
  },
  menuPaper: {
    width: 360,
    boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.16)",
    height: 391,
    maxHeight: 391
  },
  menuContentWrapper: {
    height: "100%",
    maxHeight: "100%",
    overflow: "hidden"
  },
  backToProjectsWrapper: {
    padding: theme.spacing(1.25, 1.1),
    borderBottom: "1px solid #EAEBF0"
  },
  backToProjectsText: {
    fontSize: theme.typography.pxToRem(15),
    marginLeft: 8
  },
  searchWrapper: {
    padding: theme.spacing(1.25, 1.1),
    borderBottom: "1px solid #EAEBF0"
  },
  searchInput: {
    padding: theme.spacing(1.5)
  },
  projectsListWrapper: {
    padding: theme.spacing(1, 0),
    borderBottom: "1px solid #EAEBF0",
    overflowY: "auto"
  },
  projectsList: {
    padding: 0
  },
  projectsListItem: {
    padding: theme.spacing(0.93, 2.25)
  },
  projectsListItemText: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: theme.typography.fontWeightRegular,
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
    color: "#202124",
    maxWidth: 230
  },
  projectsListItemDate: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: theme.typography.fontWeightRegular,
    color: "#7D7E82"
  },
  newProjectWrapper: {
    padding: theme.spacing(1, 0)
  },
  newProjectButton: {
    width: "100%",
    color: theme.palette.primary.main,
    padding: theme.spacing(1.25, 1.85),
    textAlign: "left",
    fontSize: theme.typography.pxToRem(15),
    justifyContent: "flex-start",
    "&:hover": {
      backgroundColor: "#F4F4F4"
    }
  },
  newProjectIcon: {
    marginRight: theme.spacing(2)
  },
  backToProjectsButton: {
    width: "100%",
    justifyContent: "inherit"
  }
}));

const dateFns = new DateFnsUtils();

const HeaderProjectsMenu = props => {
  // General
  const classes = useStyles();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  // Custom
  const productTour = useProductTour();

  // States
  const [menuOpen, setMenuOpen] = useState(false);
  const [creatingProject, setCreatingProject] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState("");
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [projects, setProjects] = useState([]);

  // Refs
  const menuButtonRef = useRef(undefined);

  // Callback
  const onNewProjectClick = () => {
    setCreatingProject(true);
    API.post("/v1/projects", {
      name: "Untitled project",
      date: dateFns.format(new Date(), "dd/MM/yyyy"),
      enable_vehicles: true
    })
      .then(response => {
        setMenuOpen(false);
        window.mixpanel && window.mixpanel.track("Create project");
        enqueueSnackbar(`Project "${response.data.name}" created.`);
        history.push(`/${response.data.id}?focusTitle`);
      })
      .catch(err => {
        console.log(err);
        enqueueSnackbar(`Something went wrong, couldn't create a project.`);
      })
      .finally(() => {
        setCreatingProject(false);
      });
  };

  const loadProjects = useCallback(() => {
    setLoadingProjects(true);
    API.get(
      `/v1/projects?status=created,preparing,optimized,reoptimized,published,processing,error,in_progress&page=0`
    )
      .then(response => {
        setProjects(response.data.projects);
        setFilteredProjects(response.data.projects);
      })
      .catch(err => console.log(err))
      .finally(() => {
        setLoadingProjects(false);
      });
  }, []);

  return (
    <>
      <ButtonBase
        disableRipple
        ref={menuButtonRef}
        className={clsx(classes.menuButton, menuOpen && classes.menuButtonOpen)}
        onClick={() => setMenuOpen(true)}
        disabled={creatingProject || productTour.hasActiveTourCurrently()}
      >
        <RoutetitanLogo className={classes.routetitanLogo} fontSize="small" />
        <ArrowDropDownIcon className={classes.projectsMenuArrowIcon} />
      </ButtonBase>
      <Popover
        disableBackdropClick={creatingProject}
        open={menuOpen}
        onClose={() => {
          setProjects([]);
          setMenuOpen(false);
        }}
        onEntered={() => {
          loadProjects();
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left"
        }}
        anchorEl={menuButtonRef.current}
        PaperProps={{
          elevation: 0
        }}
        classes={{
          paper: classes.menuPaper
        }}
      >
        <Grid
          container
          direction="column"
          className={classes.menuContentWrapper}
        >
          <Grid
            item
            container
            alignItems="center"
            className={classes.backToProjectsWrapper}
          >
            <Button
              onClick={() => {
                history.replace("/projects");
              }}
              className={classes.backToProjectsButton}
            >
              <ArrowBackIcon fontSize="small" />
              <Typography className={classes.backToProjectsText}>
                Back to projects
              </Typography>
            </Button>
          </Grid>

          <Grid item className={classes.searchWrapper}>
            <OutlinedInput
              placeholder={"Switch to..."}
              fullWidth
              classes={{ input: classes.searchInput }}
              onChange={event => {
                setSearchInputValue(event.target.value.toLowerCase());
                setFilteredProjects(
                  projects.filter(project =>
                    project.name
                      .toLowerCase()
                      .includes(event.target.value.toLowerCase())
                  )
                );
              }}
              disabled={projects.length === 0}
            />
          </Grid>
          <Grid
            item
            container={loadingProjects}
            alignItems={(loadingProjects && "center") || undefined}
            alignContent={(loadingProjects && "center") || undefined}
            justify={(loadingProjects && "center") || undefined}
            xs
            className={classes.projectsListWrapper}
          >
            {loadingProjects && <CircularProgress size={28} />}
            <List className={classes.projectsList}>
              {!loadingProjects &&
                filteredProjects.map(project => {
                  return (
                    <ListItem
                      key={project.id}
                      button
                      onClick={() => {
                        setMenuOpen(false);
                        history.push(`/${project.id}`);
                      }}
                      className={classes.projectsListItem}
                    >
                      <Grid container wrap="nowrap">
                        <Grid item xs>
                          <ListItemText
                            disableTypography
                            className={classes.projectsListItemText}
                          >
                            <FormattedText
                              text={project.name}
                              query={searchInputValue}
                            />
                          </ListItemText>
                        </Grid>
                        <Grid item>
                          <ListItemText
                            disableTypography
                            className={classes.projectsListItemDate}
                          >
                            {project.project_date &&
                              dateFns.format(
                                new Date(parseInt(project.project_date, 10)),
                                "dd MMM yyyy"
                              )}
                          </ListItemText>
                        </Grid>
                      </Grid>
                    </ListItem>
                  );
                })}
            </List>
          </Grid>
          <Grid item className={classes.newProjectWrapper}>
            <ButtonBase
              className={classes.newProjectButton}
              onClick={onNewProjectClick}
              disabled={creatingProject}
            >
              <AddCircleOutlineIcon
                fontSize="small"
                color="primary"
                className={classes.newProjectIcon}
              />
              New project
            </ButtonBase>
          </Grid>
        </Grid>
      </Popover>
    </>
  );
};

export default HeaderProjectsMenu;
